import { updateConsents } from "@flow/common/consent_logic";
import { Consent, PublicCampaignTarget } from "@flow/common/types";
import { ConsentInputComponentBlock } from "@flow/frontend/common/ConsentInputComponentBlock";
import { postFileToPresignedUrl } from "@flow/frontend/common/post_file";
import { captchaExecute, loadCaptcha } from "@flow/frontend/member/captcha";
import { ActionComponentWrapper } from "@flow/frontend/member/components/ActionComponentWrapper";
import { CampaignTargetLookup } from "@flow/frontend/member/components/CampaignTargetLookup";
import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { ProgressBar } from "@flow/frontend/member/components/ProgressBar";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { ActionComponentProps, mergeContexts } from "@flow/frontend/member/plugin_api";
import { bug } from "@flow/lib/error";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Tab,
  Typography,
  accordionSummaryClasses,
  lighten,
  Stack,
} from "@mui/material";
import DOMPurify from "dompurify";
import React from "react";
import { ActionCustomData } from "../../common/action_types";
import { submitMemberEmail, submitMemberVideo } from "../../common/routes";
import { SendAMessageContactForm } from "./SendAMessageContactForm";
import { SendAMessageEmailForm } from "./SendAMessageEmailForm";
import { SendAMessageVideoForm } from "./SendAMessageVideoForm";
import { partitionConsentConfigs } from "@flow/frontend/member/member_utils";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";

type ContactMethod = "email" | "video";

export function SendAMessageActionComponent(props: ActionComponentProps<ActionCustomData>) {
  // this is idiotic, but i'm avoiding Object.values(), which doesn't exist in IE
  const available_methods: Array<ContactMethod> = [];
  if (props.custom.enabled_contact_methods.email) available_methods.push("email");
  if (props.custom.enabled_contact_methods.video) available_methods.push("video");

  const formRef = React.useRef<HTMLFormElement>(null);

  // keep local state of member, since we may add data to it
  const [submitting, setSubmitting] = React.useState(false);
  const [member, setMember] = React.useState(props.ctx.member);
  const [memberMessageSubject, setMemberMessageSubject] = React.useState(
    props.custom.sam_prefill.enabled ? props.custom.sam_prefill.subject : ""
  );
  const [memberMessageBody, setMemberMessageBody] = React.useState(
    props.custom.sam_prefill.enabled ? props.custom.sam_prefill.body : ""
  );

  const [testMode, setTestMode] = React.useState<boolean>(props.isStaff);
  // target is undefined until the CampaignTargetLookup component has returned it
  const [targets, setTargets] = React.useState<PublicCampaignTarget[] | undefined>(undefined);
  const [uploadProgress, setUploadProgress] = React.useState<undefined | [number, number]>(undefined);
  const [commonConsents, setCommonConsents] = React.useState<Consent[]>([]);
  const [method, setMethod] = React.useState<ContactMethod>(available_methods[0]);
  const [video, setVideo] = React.useState<Blob | undefined>(undefined);
  const [videoConsents, setVideoConsents] = React.useState<Consent[]>([]);
  const [videoIntroduction, setVideoIntroduction] = React.useState(props.custom.video_email_introduction_template);
  const [implicitCommonConsentConfigs, explicitCommonConsentConfigs] = partitionConsentConfigs(
    props.custom.consent_configs.common
  );
  const [implicitVideoConsentConfigs, explicitVideoConsentConfigs] = partitionConsentConfigs(
    props.custom.consent_configs.video
  );
  const [alreadyConsentedTo, setAlreadyConsentedTo] = React.useState<Consent[]>(
    props.takenAction ? [] : props.consents || []
  );
  const [useMemberPrefill, setUseMemberPrefill] = React.useState<boolean>(
    !props.takenAction && props.redacted_prefill !== undefined && props.redacted_prefill.has_email
  );

  // run on component mount
  React.useEffect(() => {
    loadCaptcha();
  }, []);

  function rejectPrefill(e: React.MouseEvent) {
    e.preventDefault();
    setAlreadyConsentedTo([]);
    setUseMemberPrefill(false);
  }

  function isActionReadyToSubmit() {
    return (
      targets !== undefined &&
      ((method === "email" && memberMessageSubject !== "") || (method === "video" && video !== undefined))
    );
  }

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    if (!formRef.current?.checkValidity()) {
      return;
    }
    // target should have been selected before submit is possible
    if (targets === undefined) return bug();

    setSubmitting(true);

    const allCommonConsents = updateConsents(alreadyConsentedTo, commonConsents);
    // newCommonConsents is passed to the plugin backend - it is the list of consents a member saw on this step.
    // it is recorded in the DB and passed to the CRM.
    // commonConsents is passed to goToNextStep - it is the list of all consents a member has given, including ones for
    // other plugins.  It is used for consents cookie.

    // send an updated journey context to the backend, with member details filled in
    const context = mergeContexts(props.ctx, { member });

    await captchaExecute("message_submit").then((token) => {
      if (method === "email") {
        const plainText = htmlToPlainText(memberMessageBody);

        submitMemberEmail
          .call({
            context,
            actionGuid: props.action.guid,
            action_variant_idx: props.variant_idx,
            use_prefill_member_data: useMemberPrefill,
            campaignTargetUniqueIds: targets.map((t) => t.unique_id),
            subject: memberMessageSubject,
            message: plainText,
            consents: commonConsents,
            captchaToken: token,
            testMode,
          })
          .then(() => {
            props.dispatch.gotoNextStep("completed", context, allCommonConsents);
          });
      } else if (method === "video") {
        if (video === undefined) return bug("video should exist at submission time");

        // allVideoConsents are passed to the next step - its allCommonConsents (which includes all already seen consents) + videoConsents
        const allVideoConsents = [...videoConsents, ...allCommonConsents];

        /* Two-stage process. submitMemberVideo records the action
         * metadata, and returns a presigned S3 upload URL. then
         * postVideoToPresignedUrl posts the actual video data
         */
        submitMemberVideo
          .call({
            context,
            message: {
              introduction: videoIntroduction,
            },
            actionGuid: props.action.guid,
            use_prefill_member_data: useMemberPrefill,
            action_variant_idx: props.variant_idx,
            campaignTargetUniqueIds: targets.map((t) => t.unique_id),
            mimeType: video.type,
            consents: [...videoConsents, ...commonConsents],
            captchaToken: token,
            testMode,
          })
          .then((signedUploadUrl) => {
            if (signedUploadUrl === undefined) {
              props.dispatch.gotoNextStep("completed", context, allVideoConsents);
            } else {
              postFileToPresignedUrl(video, signedUploadUrl, (done, total) => {
                setUploadProgress([done, total]);
              }).then(() => {
                // done!
                console.log("Upload successful");
                props.dispatch.gotoNextStep("completed", context, allVideoConsents);
              });
            }
          });
      } else {
        return bug("missing state handler");
      }
      return;
    });
    return;
  }

  if (uploadProgress !== undefined) {
    return <ProgressBar progress={uploadProgress} label={"Uploading..."} />;
  }

  if (targets === undefined && props.custom.decision_target) {
    return (
      <ActionComponentWrapper>
        <MainContainer>
          {props.custom.landing_blurb && (
            <>
              <Typography
                sx={{ typography: { xs: "subtitle1", md: "h5" } }}
                color={memberTheme.palette.primary.main}
                dangerouslySetInnerHTML={{ __html: props.custom.landing_blurb }}
              />
              <Divider />
            </>
          )}
          <CampaignTargetLookup
            member={member}
            onUpdateMember={setMember}
            targetProviderConfig={props.custom.decision_target}
            onGetTarget={setTargets}
          />
        </MainContainer>
      </ActionComponentWrapper>
    );
  }

  const DecisionMakerTitle = () => (
    <Typography variant="subtitle1" sx={{ marginBottom: "1.5rem" }}>
      To: {targets?.map((t) => t.name).join(", ")}
    </Typography>
  );

  return (
    <ActionComponentWrapper>
      <>
        {props.isStaff && (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: lighten(memberTheme.palette.error.main, 0.85),
              padding: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <FormGroup>
              <FormControlLabel
                sx={{ marginBottom: "0px" }}
                control={<Checkbox checked={testMode} onChange={({ target: { checked } }) => setTestMode(checked)} />}
                label="Leave checked if you don't want your email to be sent to any decision makers. This only affects the staff team, members never see this tick box and their emails will be sent as normal."
              />
            </FormGroup>
          </Paper>
        )}
        <MainContainer>
          <Stack component={"form"} ref={formRef} spacing={3} sx={{ borderRadius: "4px" }}>
            <Typography sx={{ typography: { xs: "subtitle1", md: "h5" } }} color={memberTheme.palette.primary.main}>
              {props.custom.title}
            </Typography>

            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: props.custom.action_blurb }} />

            <Divider />

            <Accordion elevation={0} sx={{ backgroundColor: "transparent" }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="writing-tips-content"
                sx={{
                  padding: "0",
                  minHeight: "0",
                  [`& .${accordionSummaryClasses.content}`]: { margin: "0 !important" },
                  [`& .${accordionSummaryClasses.expanded}`]: { margin: "0 !important" },
                }}
              >
                Writing tips
              </AccordionSummary>
              <AccordionDetails sx={{ padding: "0 !important" }}>
                <Typography variant="body1" dangerouslySetInnerHTML={{ __html: props.custom.action_info2 }} />
              </AccordionDetails>
            </Accordion>

            <Divider />

            {available_methods.length <= 0 && (
              <Typography variant="body2">Error: no contact methods enabled for this action</Typography>
            )}
            {available_methods.length > 0 && targets === undefined && props.custom.decision_target ? (
              <CampaignTargetLookup
                member={member}
                onUpdateMember={setMember}
                targetProviderConfig={props.custom.decision_target}
                onGetTarget={setTargets}
              />
            ) : (
              <>
                <TabContext value={method}>
                  <TabList
                    onChange={(_, newValue) => setMethod(newValue)}
                    variant="fullWidth"
                    aria-label="contact methods"
                  >
                    {props.custom.enabled_contact_methods.email && <Tab label="Email" value="email" />}
                    {props.custom.enabled_contact_methods.video && <Tab label="Video" value="video" />}
                  </TabList>
                  <TabPanel value="email" sx={{ padding: "0" }}>
                    <DecisionMakerTitle />
                    <SendAMessageEmailForm
                      samPrefillEnabled={props.custom.sam_prefill.enabled}
                      memberMessageSubject={memberMessageSubject}
                      memberMessageBody={memberMessageBody}
                      onMemberMessageSubjectChange={setMemberMessageSubject}
                      onMemberMessageBodyChange={setMemberMessageBody}
                    />
                  </TabPanel>
                  <TabPanel value="video" sx={{ padding: "0" }}>
                    <DecisionMakerTitle />
                    <SendAMessageVideoForm
                      video={video}
                      onGotVideo={setVideo}
                      videoIntroduction={videoIntroduction}
                      onVideoIntroductionChange={setVideoIntroduction}
                      presubmitVideoText={props.custom.presubmit_video_text}
                      videoIntroductionTemplate={props.custom.presubmit_video_text}
                    />
                  </TabPanel>
                </TabContext>
                <SendAMessageContactForm
                  useMemberPrefill={useMemberPrefill}
                  onRejectPrefill={rejectPrefill}
                  redactedPrefill={props.redacted_prefill}
                  isStaff={props.isStaff}
                  takenAction={props.takenAction}
                  member={member}
                  onMemberChange={setMember}
                />
              </>
            )}

            <Divider />

            {submitting ? (
              <MemberLoadingSpinner />
            ) : (
              <>
                {props.custom.media_info && (
                  <>
                    <Typography variant="caption" dangerouslySetInnerHTML={{ __html: props.custom.media_info }} />
                    <Divider />
                  </>
                )}
                <ConsentInputComponentBlock
                  consentConfigs={explicitCommonConsentConfigs}
                  alreadyConsentedTo={alreadyConsentedTo}
                  updateConsents={(changedConsents) => setCommonConsents((ccs) => updateConsents(ccs, changedConsents))}
                />
                {method === "video" && (
                  <>
                    <ConsentInputComponentBlock
                      consentConfigs={explicitVideoConsentConfigs}
                      alreadyConsentedTo={alreadyConsentedTo}
                      updateConsents={(changedConsents) =>
                        setVideoConsents((vcs) => updateConsents(vcs, changedConsents))
                      }
                    />
                  </>
                )}

                <Divider />

                <Button
                  type="submit"
                  sx={{
                    width: { sm: "100%", md: "fit-content" },
                    paddingX: { md: "1rem" },
                  }}
                  onClick={handleSubmit}
                  disabled={!isActionReadyToSubmit()}
                >
                  Send Message <ChevronRightIcon />
                </Button>

                {implicitCommonConsentConfigs.length > 0 && (
                  <>
                    <ConsentInputComponentBlock
                      consentConfigs={implicitCommonConsentConfigs}
                      alreadyConsentedTo={alreadyConsentedTo}
                      updateConsents={(changedConsents) =>
                        setCommonConsents((ccs) => updateConsents(ccs, changedConsents))
                      }
                      displayTopBorder={true}
                      displayBottomBorder={true}
                    />
                  </>
                )}

                {implicitVideoConsentConfigs.length > 0 && method === "video" && (
                  <ConsentInputComponentBlock
                    consentConfigs={implicitVideoConsentConfigs}
                    alreadyConsentedTo={alreadyConsentedTo}
                    updateConsents={(changedConsents) =>
                      setVideoConsents((vcs) => updateConsents(vcs, changedConsents))
                    }
                    displayTopBorder={implicitCommonConsentConfigs.length === 0} // only show top border if there are no common consents rendered above
                    displayBottomBorder={true}
                  />
                )}
              </>
            )}
          </Stack>
        </MainContainer>
      </>
    </ActionComponentWrapper>
  );
}

export function htmlToPlainText(html: string | Node) {
  const sanitizedHtml = DOMPurify.sanitize(html);
  const tempDivElement = document.createElement("div");

  // Parse the sanitized HTML and append its nodes to the temporary div element
  const parsedNodes = new DOMParser().parseFromString(sanitizedHtml, "text/html").body.childNodes;
  const nodesArray = Array.prototype.slice.call(parsedNodes);
  for (const node of nodesArray) {
    tempDivElement.append(node.cloneNode(true));
  }
  // Helper function to handle line breaks
  function addLineBreak(node: { tagName: string }) {
    const tagName = node.tagName ? node.tagName.toLowerCase() : "";
    if (tagName === "div" || tagName === "p" || tagName === "span" || tagName === "br") {
      return "\n" + "\n"; // Add two line break for div, p and br tags
    } else if (tagName === "li") {
      return "\n";
    }
    return "";
  }

  let plainText = "";

  function extractText(node: any) {
    const nodeType = node.nodeType;
    if (nodeType === Node.TEXT_NODE) {
      plainText += node.textContent;
    } else if (nodeType === Node.ELEMENT_NODE) {
      const lineBreak = addLineBreak(node);
      plainText += lineBreak;
      for (const childNode of node.childNodes) {
        extractText(childNode);
      }
    }
  }

  extractText(tempDivElement);
  return plainText.trim();
}
